import React from "react";
const isDark = document.documentElement.classList.contains("dark");

<circle
  stroke={isDark ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))"}
/>


/* =========================================================
   COOLDOWN STOPWATCH + CONSECUTIVE LOSSES (FINAL, FIXED)
   - Stopwatch ticks every second
   - Correctly initializes even if cooldownUntil arrives later
   - Resyncs on prop change
   - Loss bars fill progressively
   - Theme-safe colors (NO Tailwind hacks)
   ========================================================= */

/* ---------------- COOLDOWN STOPWATCH ---------------- */
export const CooldownStopwatch = ({
  remainingMin = 0,
  maxMin = 0,
  cooldownUntil, // timestamp in ms
}) => {
  const safeMax = maxMin || 1;

  /* seconds remaining (authoritative state) */
  const [secondsLeft, setSecondsLeft] = React.useState(0);

  /* 🔑 SYNC EFFECT */
  React.useEffect(() => {
    if (cooldownUntil) {
      setSecondsLeft(
        Math.max(0, Math.floor((cooldownUntil - Date.now()) / 1000))
      );
    } else {
      setSecondsLeft(Math.round((remainingMin || 0) * 60));
    }
  }, [cooldownUntil, remainingMin]);

  /* ⏱ LIVE TICK EFFECT */
  React.useEffect(() => {
    if (!cooldownUntil) return;

    const interval = setInterval(() => {
      const diff = Math.max(
        0,
        Math.floor((cooldownUntil - Date.now()) / 1000)
      );
      setSecondsLeft(diff);
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const remainingMinLive = secondsLeft / 60;
  const progress = Math.max(
    0,
    Math.min(1, remainingMinLive / safeMax)
  );

  const size = 150;
  const strokeWidth = 2;
  const r = size / 2 - 18;
  const c = 2 * Math.PI * r;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="absolute inset-0 w-full h-full"
        >
          {/* outer bezel */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r + 8 - strokeWidth / 2}
            stroke={isDark ? "hsl(var(--muted))" : "hsl(var(--muted-foreground))"}
            strokeWidth={strokeWidth}
            fill="none"
          />

          {/* tick marks */}
          {Array.from({ length: 12 }).map((_, i) => {
            const angle = (i / 12) * 2 * Math.PI;
            const x1 = size / 2 + Math.cos(angle) * (r - 4);
            const y1 = size / 2 + Math.sin(angle) * (r - 4);
            const x2 = size / 2 + Math.cos(angle) * (r - 10);
            const y2 = size / 2 + Math.sin(angle) * (r - 10);
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={isDark ? "hsl(var(--muted))" : "hsl(var(--muted-foreground))"}
                strokeWidth={2}
              />
            );
          })}

          {/* progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="#f59e0b"
            strokeWidth={6}
            fill="none"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - progress)}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            className="transition-all duration-700"
          />

          {/* center dot */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={4}
            fill="hsl(var(--muted-foreground))"
          />
        </svg>

        {/* digital readout */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold text-amber-500">
            {Math.floor(secondsLeft / 60)}:
            {String(secondsLeft % 60).padStart(2, "0")}
          </div>
          <div className="text-[10px] tracking-widest text-muted-foreground">
            COOLDOWN
          </div>
          <div className="mt-1 text-[9px] tracking-widest text-muted-foreground">
            MAX: {safeMax}m
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- CONSECUTIVE LOSSES ---------------- */
export const LossStreakCells = ({ losses = 0, maxLosses = 0 }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-2">
        {Array.from({ length: maxLosses }).map((_, i) => {
          const filled = i < losses;

          return (
            <div
              key={i}
              className="w-6 h-10 rounded-md border transition-all duration-300"
              style={
                filled
                  ? {
                      borderColor: "rgb(239 68 68)", // red-500
                      backgroundColor: "rgba(239,68,68,0.4)",
                    }
                  : {
                      borderColor: "hsl(var(--muted-foreground))",
                      backgroundColor: "hsl(var(--loss-cell-unfilled-bg))",
                    }
              }
            />
          );
        })}
      </div>

      <div className="mt-2 text-sm font-semibold text-red-500">
        {losses}/{maxLosses}
      </div>
      <div className="text-[10px] tracking-widest text-muted-foreground">
        CONSECUTIVE LOSSES
      </div>
    </div>
  );
};

/* ---------------- PREVIEW ---------------- */
export default function Preview() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-row sm:flex-col gap-6 sm:gap-10 items-center">
        <CooldownStopwatch
          remainingMin={3}
          maxMin={5}
          cooldownUntil={Date.now() + 3 * 60 * 1000}
        />

        <LossStreakCells
          losses={2}
          maxLosses={5}
        />
      </div>
    </div>
  );
}
